// src/pages/HelpDesk.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getDoc, doc } from 'firebase/firestore';
import { db, auth } from '../utils/firebase';
import HostLayout from '../layouts/HostLayout';

const HelpDesk = () => {
  const navigate = useNavigate();
  const [user, loading] = useAuthState(auth);
  const [userRole, setUserRole] = useState(null);
  // const [activeTab, setActiveTab] = useState('faq'); // Removed unused state

  useEffect(() => {
    if (user && !loading) {
      const checkUserRole = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const role = userDoc.data().role || 'user';
            setUserRole(role);
            
            // Redirect admin users to admin support tickets
            if (role === 'admin') {
              navigate('/admin/support-tickets', { replace: true });
              return;
            }
            
            // Redirect team users to team help
            if (role === 'team_captain' || role === 'team_member') {
              navigate('/team/help', { replace: true });
              return;
            }
          }
        } catch (error) {
          console.error('Error checking user role:', error);
          setUserRole('user');
        }
      };
      checkUserRole();
    }
  }, [user, loading, navigate]);

  const getBackPath = () => {
    if (userRole === 'admin') {
      return '/admin';
    } else if (userRole === 'host') {
      return '/home';
    } else {
      return '/team/dashboard';
    }
  };

  const faqs = [
    {
      category: 'Account & Login',
      questions: [
        {
          q: 'How do I reset my password?',
          a: 'Click the "Forgot Password" link on the login page. You\'ll receive an email with instructions to reset your password.'
        },
        {
          q: 'Can I change my email address?',
          a: 'Yes, you can update your email address in your profile settings. You\'ll need to verify the new email address.'
        },
        {
          q: 'How do I delete my account?',
          a: 'Contact support to request account deletion. We\'ll process your request within 48 hours.'
        }
      ]
    },
    {
      category: 'Team Management',
      questions: [
        {
          q: 'How many members can be on a team?',
          a: 'Teams can have up to 10 members. The team captain can manage member invitations and permissions.'
        },
        {
          q: 'How do I become a team captain?',
          a: 'Team captains are typically the first person to create the team. Captains can transfer leadership to other members.'
        },
        {
          q: 'Can I join multiple teams?',
          a: 'Currently, you can only be a member of one team at a time. You can leave a team and join another if needed.'
        }
      ]
    },
    {
      category: 'Games & Trivia',
      questions: [
        {
          q: 'How do I find games near me?',
          a: 'Use the "Find Games" feature to discover trivia games at venues in your area. You can filter by date and location.'
        },
        {
          q: 'What if a game is cancelled?',
          a: 'Game cancellations will be communicated via email and in-app notifications. You can also check the venue\'s status.'
        },
        {
          q: 'How do I rate a game?',
          a: 'After participating in a game, you\'ll receive a QR code to scan and rate your experience. Ratings help improve future games.'
        }
      ]
    },
    {
      category: 'Venues & Locations',
      questions: [
        {
          q: 'How do venues get added to the platform?',
          a: 'Venues can register through our venue partnership program. Hosts can also suggest venues for consideration.'
        },
        {
          q: 'What if a venue is no longer hosting games?',
          a: 'Venue status is updated regularly. If you notice a venue is no longer active, please report it through support.'
        },
        {
          q: 'Can I suggest a new venue?',
          a: 'Yes! Use the support ticket system to suggest new venues. We\'ll review and potentially add them to our network.'
        }
      ]
    },
    {
      category: 'Technical Issues',
      questions: [
        {
          q: 'The app is loading slowly. What should I do?',
          a: 'Try refreshing the page, clearing your browser cache, or using a different browser. Check your internet connection.'
        },
        {
          q: 'I\'m getting an error message. What does it mean?',
          a: 'Error messages help us identify issues. Please submit a support ticket with the exact error message for assistance.'
        },
        {
          q: 'The app isn\'t working on my device.',
          a: 'Make sure you\'re using a supported browser (Chrome, Firefox, Safari, Edge) and have a stable internet connection.'
        }
      ]
    }
  ];

  const quickActions = [
    {
      title: 'Submit Support Ticket',
      description: 'Get help with any issue',
      icon: '🎫',
      action: () => navigate('/support-ticket'),
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      title: 'Find Games',
      description: 'Discover trivia games nearby',
      icon: '🔍',
      action: () => navigate('/venue-discovery'),
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      title: userRole === 'admin' ? 'Admin Dashboard' : userRole === 'host' ? 'Host Dashboard' : 'Team Dashboard',
      description: userRole === 'admin' ? 'Manage platform' : userRole === 'host' ? 'Manage games' : 'Manage team',
      icon: userRole === 'admin' ? '⚙️' : userRole === 'host' ? '🎮' : '🏆',
      action: () => navigate(getBackPath()),
      color: 'bg-purple-500 hover:bg-purple-600'
    },
    {
      title: 'Marketplace',
      description: 'Browse and schedule games',
      icon: '🛒',
      action: () => navigate('/marketplace'),
      color: 'bg-orange-500 hover:bg-orange-600'
    }
  ];

  const contactInfo = [
    {
      method: 'Email Support',
      details: 'support@triviaplug.com',
      icon: '📧',
      description: 'For general inquiries and support - respond within 24-48 hours'
    },
    {
      method: 'Support Tickets',
      details: 'Available 24/7 for urgent issues',
      icon: '🎫',
      description: 'Submit tickets through the Help Desk for faster response'
    },
    {
      method: 'Feature Requests',
      details: 'Submit via support ticket',
      icon: '💡',
      description: 'Help us improve the platform'
    }
  ];

  return (
    <HostLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(getBackPath())}
            className="flex items-center gap-2 text-green-600 hover:text-green-700 mb-4"
          >
            ← Back to {userRole === 'admin' ? 'Admin' : userRole === 'host' ? 'Home' : 'Team Dashboard'}
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Help Desk</h1>
          <p className="text-gray-600">
            Find answers to common questions and get the support you need
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.action}
              className={`${action.color} text-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-left`}
            >
              <div className="text-3xl mb-3">{action.icon}</div>
              <h3 className="text-lg font-semibold mb-1">{action.title}</h3>
              <p className="text-sm opacity-90">{action.description}</p>
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* FAQ Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
                <div className="text-sm text-gray-500">
                  {faqs.reduce((total, category) => total + category.questions.length, 0)} questions
                </div>
              </div>

              {/* FAQ Categories */}
              <div className="space-y-6">
                {faqs.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="border-b border-gray-200 pb-6 last:border-b-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{category.category}</h3>
                    <div className="space-y-4">
                      {category.questions.map((item, questionIndex) => (
                        <details key={questionIndex} className="group">
                          <summary className="flex items-center justify-between cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                            <span className="font-medium text-gray-900">{item.q}</span>
                            <span className="text-gray-500 group-open:rotate-180 transition-transform">▼</span>
                          </summary>
                          <div className="p-4 bg-white border border-gray-200 rounded-lg mt-2">
                            <p className="text-gray-700">{item.a}</p>
                          </div>
                        </details>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact & Support Sidebar */}
          <div className="space-y-6">
            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Support</h3>
              <div className="space-y-4">
                {contactInfo.map((contact, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="text-2xl">{contact.icon}</div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{contact.method}</h4>
                      <p className="text-sm text-gray-600">{contact.details}</p>
                      <p className="text-xs text-gray-500 mt-1">{contact.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI-Powered Support */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">🤖 AI-Powered Support</h3>
              <div className="text-sm text-blue-800 space-y-2">
                <div>• Instant ticket categorization</div>
                <div>• Smart priority assignment</div>
                <div>• Automated solution suggestions</div>
                <div>• 24/7 availability</div>
              </div>
              <button
                onClick={() => navigate('/support-ticket')}
                className="w-full mt-4 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition"
              >
                Submit AI Ticket
              </button>
            </div>

            {/* System Status */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">System Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Platform</span>
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-green-600">Operational</span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Games</span>
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-green-600">Operational</span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Payments</span>
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-green-600">Operational</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HostLayout>
  );
};

export default HelpDesk;
